import { Bar, BarChart } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/core/components/ui/chart";
import { chartColor } from "@/core/components/ui/chart-colors";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { cn } from "@/core/utils/func/cn";

export type StatisticsTotalRevenueChartDatum = {
  month: string;
  primary: number;
  secondary: number;
};

export type StatisticsTotalRevenueCardProps = {
  title: string;
  description: string;
  changePercentage: string;
  chartData: StatisticsTotalRevenueChartDatum[];
  primaryLabel?: string;
  secondaryLabel?: string;
  className?: string;
};

export const StatisticsTotalRevenueCard = ({
  title,
  description,
  changePercentage,
  chartData,
  primaryLabel,
  secondaryLabel,
  className,
}: StatisticsTotalRevenueCardProps) => {
  const chartConfig = {
    primary: {
      label: primaryLabel,
      color: chartColor("accent"),
    },
    secondary: {
      label: secondaryLabel,
      color: chartColor("accent", 0.2),
    },
  } satisfies ChartConfig;

  return (
    <Card className={cn("relative justify-between", className)}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-1 rounded-t-[20px] bg-gradient-accent"
      />
      <CardHeader>
        <div className="flex items-center gap-2">
          <NoTranslate as="span">
            <CardTitle className="text-lg font-semibold">{title}</CardTitle>
          </NoTranslate>
          <NoTranslate as="span" className="text-base">
            {changePercentage}
          </NoTranslate>
        </div>
        <CardDescription className="text-base text-muted-foreground">
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[124px] w-full">
          <BarChart
            accessibilityLayer
            data={chartData}
            barSize={12}
            margin={{
              left: 0,
              right: 0,
            }}
            barGap={0}
          >
            <Bar
              dataKey="primary"
              fill="var(--color-primary)"
              radius={[12, 12, 0, 0]}
            />
            <Bar
              dataKey="secondary"
              fill="var(--color-secondary)"
              radius={[12, 12, 0, 0]}
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
